import axios from 'axios';

// Determine API base dynamically
// REACT_APP_API_BASE should point to the backend ROOT (without trailing /api)
// Examples:
// - Development: http://localhost:5000
// - Production via Nginx prefix: /talentmesh-be
const resolveApiBase = () => {
  const envBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim() !== '') return envBase;
  // Fallbacks
  const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
  return isLocal ? 'http://localhost:5000' : '/talentmesh-be';
};

const API_BASE = resolveApiBase().replace(/\/$/, '');
// If env already ends with /api, don't append again
const API_PREFIX = /\/api$/i.test(API_BASE) ? API_BASE : `${API_BASE}/api`;

// Create axios instance with proper configuration
const api = axios.create({
  baseURL: API_PREFIX,
  withCredentials: true, // ✅ CRITICAL: Send session cookies
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} from ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

// ==================== AUTHENTICATION API ====================
export const authAPI = {
  checkSession: () => api.get('/auth/session'),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  testSession: () => api.get('/auth/test-session'),
  debugSession: () => api.get('/auth/debug'),
  
  // ✅ FIXED: Microsoft Login with proper handling
  microsoftLogin: (role = 'admin') => {
    return new Promise((resolve, reject) => {
      console.log(`🔐 Starting Microsoft OAuth for role: ${role}`);
      
      // Create popup window
      const popupUrl = `${API_PREFIX}/auth/microsoft/login?role=${role}`;
      const popup = window.open(
        popupUrl,
        'microsoftLogin',
        'width=500,height=700,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,status=no'
      );

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      // Listen for messages from popup
      const messageListener = (event) => {
        // Accept messages only from our current page origin (Nginx host)
        const allowedOrigin = window.location.origin;
        if (event.origin !== allowedOrigin) return;
        
        console.log('🔐 Received message from popup:', event.data);
        
        if (event.data.type === 'MICROSOFT_AUTH_SUCCESS') {
          window.removeEventListener('message', messageListener);
          popup.close();
          console.log('✅ Microsoft auth successful');
          resolve(event.data);
        } else if (event.data.type === 'MICROSOFT_AUTH_ERROR') {
          window.removeEventListener('message', messageListener);
          popup.close();
          console.error('❌ Microsoft auth failed:', event.data.error);
          reject(new Error(event.data.error || 'Microsoft authentication failed'));
        }
      };

      window.addEventListener('message', messageListener);

      // Handle popup closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          console.log('⚠️ Microsoft auth popup closed by user');
          reject(new Error('Authentication cancelled by user'));
        }
      }, 1000);

      // Cleanup after 5 minutes
      setTimeout(() => {
        if (!popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          popup.close();
          reject(new Error('Authentication timeout'));
        }
      }, 300000); // 5 minutes
    });
  },

  // ✅ Alternative: Direct redirect method (fallback)
  microsoftLoginRedirect: (role = 'admin') => {
    console.log(`🔐 Redirecting to Microsoft OAuth for role: ${role}`);
    window.location.href = `${API_PREFIX}/auth/microsoft/login?role=${role}`;
  },

  getProfile: () => api.get('/auth/microsoft/profile'),
  getCalendar: () => api.get('/auth/microsoft/calendar'),
  createTeamsMeeting: (meetingData) => api.post('/auth/microsoft/create-meeting', meetingData),
  createInterviewMeeting: (meetingData) => api.post('/auth/microsoft/create-interview-meeting', meetingData),
};

// ==================== ADMIN API ====================
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAnalytics: () => api.get('/admin/analytics'),
   // ✅ NEW: Job Tracking APIs
  getJobTrackingStats: (jobId) => api.get(`/admin/jobs/${jobId}/tracking`),
  getAllJobsTrackingOverview: (params = {}) => api.get('/admin/jobs/tracking-overview', { params }),
  getQuickJobStats: () => api.get('/admin/jobs/quick-stats'),
  getJobs: (params = {}) => api.get('/admin/jobs', { params }),
  createJob: (jobData) => api.post('/admin/jobs', jobData),
  updateJob: (jobId, jobData) => api.put(`/admin/jobs/${jobId}`, jobData),
  deleteJob: (jobId) => api.delete(`/admin/jobs/${jobId}`),
  getVendors: () => api.get('/admin/vendors'),
  getPanelists: () => api.get('/admin/panelists'),
  getCandidates: (params = {}) => api.get('/admin/candidates', { params }),
  assignVendor: (jobId, vendorId) => api.post(`/admin/jobs/${jobId}/assign-vendor`, { vendor_id: vendorId }),
  assignPanelist: (jobId, panelistId) => api.post(`/admin/jobs/${jobId}/assign-panelist`, { panelist_id: panelistId }),
  getJobDetails: (jobId) => api.get(`/admin/jobs/${jobId}`),
  getJobAssignments: (jobId) => api.get(`/admin/jobs/${jobId}/assignments`),
  removeAssignment: (jobId, assignmentData) => api.delete(`/admin/jobs/${jobId}/remove-assignment`, { data: assignmentData }),
  blockUser: (userId) => api.post(`/admin/users/${userId}/block`),
  unblockUser: (userId) => api.post(`/admin/users/${userId}/unblock`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  createUser: (userData) => api.post('/admin/users/create', userData),

  // Add this to the adminAPI object
getCVReviews: (params = {}) => {
  console.log('📝 API: Getting CV reviews with params:', params);
  return api.get('/admin/cv-reviews', { params });
},

// Add to adminAPI in api.js
searchInterviews: (searchParams) => {
  console.log('🔍 API: Searching interviews with params:', searchParams);
  return api.post('/admin/interviews/search', searchParams);
},

getInterviewSearchSuggestions: (type = 'job_titles') => {
  console.log('💡 API: Getting interview search suggestions for:', type);
  return api.get(`/admin/interviews/suggestions?type=${type}`);
},

// 🔥 NEW: Enhanced slot management
  forceDeleteExpertSlot: (slotId) => {
    console.log('🗑️ Admin force deleting slot:', slotId);
    return api.delete(`/admin/expert-slots/${slotId}/force-delete`);
  },

  forceFreeExpertSlot: (slotId, data = {}) => {
    console.log('🔓 Admin freeing slot:', slotId, data);
    return api.post(`/admin/expert-slots/${slotId}/force-free`, data);
  },



  // Expert Slots Management
  getExpertSlots: (params = {}) => api.get('/admin/expert-slots', { params }),
  createExpertSlot: (slotData) => api.post('/admin/expert-slots', slotData),
  updateExpertSlot: (slotId, slotData) => api.put(`/admin/expert-slots/${slotId}`, slotData),
  deleteExpertSlot: (slotId) => api.delete(`/admin/expert-slots/${slotId}`),

  // ✅ NEW: Candidate Search Methods
// ✅ FIXED: Change from POST to GET with proper query parameters
searchCandidates: async (searchParams) => {
    try {
        console.log('🔍 API: Searching candidates with params:', searchParams);
        
        // Build URL parameters properly
        const params = new URLSearchParams();
        
        // Add all search parameters as query params
        if (searchParams.skills) params.append('skills', searchParams.skills);
        if (searchParams.location) params.append('location', searchParams.location);
        if (searchParams.min_experience !== undefined) params.append('min_experience', searchParams.min_experience);
        if (searchParams.max_experience !== undefined) params.append('max_experience', searchParams.max_experience);
        if (searchParams.willing_to_relocate !== null && searchParams.willing_to_relocate !== undefined) {
            params.append('willing_to_relocate', searchParams.willing_to_relocate.toString());
        }
        
        // 🔥 CRITICAL: Add pagination and control parameters
        params.append('page', searchParams.page || 1);
        params.append('per_page', searchParams.per_page || 1000);
        params.append('sort_by', searchParams.sort_by || 'relevance');
        params.append('sort_order', searchParams.sort_order || 'desc');
        
        // 🔥 CRITICAL: Add show_all flag
        if (searchParams.show_all !== undefined) {
            params.append('show_all', searchParams.show_all.toString());
        }

        // Make GET request with query parameters
        const response = await api.get(`/admin/candidates/search?${params.toString()}`);
        
        console.log('📡 API Response:', {
            status: response.data.status,
            candidateCount: response.data.candidates?.length || 0,
            totalFromPagination: response.data.pagination?.total || 0
        });
        
        return response;
        
    } catch (error) {
        console.error('❌ API Error:', error);
        throw error;
    }
},

  // Panelist Availability Management
  getPanelistAvailabilityOverview: (panelistId) => api.get(`/admin/panelists/${panelistId}/availability-overview`),
  getPanelistAvailabilityOverviewAll: () => api.get('/admin/panelists/availability'),
  getPanelistSlots: (panelistId, params = {}) => api.get(`/admin/panelists/${panelistId}/availability-slots`, { params }),
  createPanelistSlotWithTeams: (panelistId, slotData) => api.post(`/admin/panelists/${panelistId}/availability-slots`, slotData),

  // Interview Management
  getInterviews: (params = {}) => api.get('/admin/interviews', { params }),
  getUpcomingInterviews: () => api.get('/admin/interviews/upcoming'),
  getCompletedInterviews: (params = {}) => api.get('/admin/interviews/completed', { params }),
  getInterviewDetails: (interviewId) => api.get(`/admin/interviews/${interviewId}/details`),
  getInterviewStatistics: () => api.get('/admin/interviews/statistics'),
  cancelInterview: (interviewId, reason) => api.post(`/admin/interviews/${interviewId}/cancel`, { reason }),

  // ✅ SHARED INTERVIEW ENDPOINTS (REUSE FROM EXISTING)
  getSharedUpcomingInterviews: () => api.get('/interviews/upcoming'),
  getSharedCompletedInterviews: (params = {}) => api.get('/interviews/completed', { params }),
  getSharedInterviewDetails: (interviewId) => api.get(`/interviews/${interviewId}/details`),

  // Debug
};

// ==================== HIRING MANAGER API ====================
export const managerAPI = {
  getDashboard: () => api.get('/manager/dashboard'),

  // Requirements
  getRequirements: (params = {}) => api.get('/manager/requirements', { params }),
  getRequirement: (id) => api.get(`/manager/requirements/${id}`),
  createRequirement: (payload) => api.post('/manager/requirements', payload),
  updateRequirement: (id, payload) => api.put(`/manager/requirements/${id}`, payload),
  publishRequirement: (id) => api.post(`/manager/requirements/${id}/publish`),
  closeRequirement: (id, outcome) => api.post(`/manager/requirements/${id}/close`, { outcome }),
  deleteRequirement: (id) => api.delete(`/manager/requirements/${id}`),

  // Candidates put forward against those requirements
  getRequirementCandidates: (id, params = {}) =>
    api.get(`/manager/requirements/${id}/candidates`, { params }),
  getCandidates: (params = {}) => api.get('/manager/candidates', { params }),
  getCandidate: (candidateId) => api.get(`/manager/candidates/${candidateId}`),

  getJobTitles: () => api.get('/manager/job-titles'),
};

// ==================== CV / RESUME API ====================
export const cvAPI = {
  /**
   * Parse a resume and check it for duplicates in one call.
   *
   * Returns the extracted fields, a duplicate report, and an upload_token —
   * the token lets the submit step reuse the file already on the server rather
   * than transferring it a second time.
   */
  parseResume: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadFileWithProgress('/cv/parse-resume', formData, onProgress);
  },

  /** Describe a candidate's resume without transferring the file. */
  getResumeMeta: (candidateId) => api.get(`/cv/candidates/${candidateId}/resume/meta`),

  /**
   * Fetch a candidate's resume as a Blob.
   *
   * Deliberately fetched through axios rather than pointed at by an <iframe> or
   * <a href>. Create React App's dev server only proxies requests that do *not*
   * accept HTML — a frame or link navigation sends `Accept: text/html`, so the
   * dev server answers with index.html and the React app renders inside the
   * frame instead of the PDF. An XHR is proxied correctly, carries the session
   * cookie, and behaves the same in production.
   */
  fetchResumeBlob: (candidateId) =>
    api.get(`/cv/candidates/${candidateId}/resume`, { responseType: 'blob' }),

  /**
   * A short-lived signed URL for a candidate's resume.
   *
   * Resumes live in a Cloud Storage bucket with public access blocked, so this
   * is the only way a browser reads one without the bytes passing through the
   * API. The server still authorizes the caller before issuing it, and the URL
   * expires — it is not a link that can be filed away and shared.
   */
  getResumeDownloadUrl: (candidateId) =>
    api.get(`/cv/candidates/${candidateId}/resume/url`, { params: { download: 1 } }),

  getDuplicateInfo: (candidateId) => api.get(`/cv/duplicates/${candidateId}`),
  resolveDuplicate: (candidateId, action, notes) =>
    api.post(`/cv/duplicates/${candidateId}/resolve`, { action, notes }),
  getCoolOffStatus: (candidateId) => api.get(`/cv/cool-off-status/${candidateId}`),
};

// ==================== VENDOR API (ENHANCED WITH TEAMS BOOKING) ====================
export const vendorAPI = {
  // Manager requirements open for sourcing
  getRequirements: (params = {}) => api.get('/vendor/requirements', { params }),
  startSourcing: (requirementId) => api.post(`/vendor/requirements/${requirementId}/start-sourcing`),

  // In vendorAPI object
getDashboard: (params = {}) => api.get('/vendor/dashboard', { params }),

  getAllPanelists: () => api.get('/vendor/panelists'),
  getJobDetails: (jobId) => api.get(`/vendor/jobs/${jobId}`),

  // CV Upload (Legacy and New)
  uploadCandidateCV: (jobId, formData) => api.post(`/vendor/jobs/${jobId}/upload-candidate-legacy`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadCandidateWithS3ML: (jobId, formData) => api.post(`/vendor/jobs/${jobId}/upload-candidate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  /**
   * Submit a candidate against a job.
   *
   * `fields` may carry `upload_token` from cvAPI.parseResume instead of a file,
   * in which case the resume already on the server is reused.
   */
  submitCandidate: (jobId, fields, file = null, onProgress) => {
    const formData = new FormData();
    Object.entries(fields || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value);
      }
    });
    if (file) formData.append('cv_file', file);
    return uploadFileWithProgress(`/vendor/jobs/${jobId}/upload-candidate`, formData, onProgress);
  },

  getCandidateDetails: (candidateId) => api.get(`/vendor/candidates/${candidateId}`),
  getPanelistsForJob: (jobId) => api.get(`/vendor/jobs/${jobId}/panelists`),
  getVendorStatistics: () => api.get('/vendor/statistics'),

  // ✅ INTERVIEW SCHEDULING & BOOKING (EXISTING + NEW TEAMS INTEGRATION)
  scheduleInterview: (interviewData) => api.post('/vendor/interviews/schedule', interviewData),
  getAvailableSlots: (params = {}) => api.get('/vendor/available-slots', { params }),

  // ✅ EXISTING BOOKING METHODS (MAINTAINED)
  bookInterviewSlot: (slotId, bookingData) => api.post(`/vendor/book-slot/${slotId}`, bookingData),
  bookInterviewSlotWithCandidate: (bookingData) => api.post('/vendor/interviews/book-slot', bookingData),

  // ✅ NEW: ENHANCED TEAMS BOOKING WITH INVITATIONS
  bookInterviewWithTeams: (bookingData) => {
    console.log('🎥 API: Booking interview with Teams meeting:', bookingData);
    return api.post('/vendor/interviews/book', bookingData);
  },

  // ✅ NEW: Interview Management
  getInterviews: (params = {}) => api.get('/vendor/interviews', { params }),

  // Interview Management
  getUpcomingInterviews: () => api.get('/interviews/upcoming'),
  getCompletedInterviews: (params = {}) => api.get('/interviews/completed', { params }),
  getInterviewDetails: (interviewId) => api.get(`/interviews/${interviewId}/details`),
  getInterviewFeedback: (interviewId) => api.get(`/interviews/${interviewId}/feedback`),

  // ✅ EXISTING ADDITIONAL METHODS
  getPanelistAvailability: (panelistId, params = {}) => api.get(`/vendor/panelists/${panelistId}/availability`, { params }),
  rescheduleInterview: (interviewId, rescheduleData) => api.put(`/vendor/interviews/${interviewId}/reschedule`, rescheduleData),
  getMyInterviewBookings: (params = {}) => api.get('/vendor/interviews/my-bookings', { params }),

  // Debug
};

  // ==================== PANELIST API (ENHANCED) ====================
export const panelistAPI = {
  // Core Dashboard & Jobs
  getDashboard: () => api.get('/panelist/dashboard'),
  getAssignedJobs: (params = {}) => api.get('/panelist/jobs', { params }),

  // CV Review System
  getCandidateReview: (candidateId) => api.get(`/panelist/candidates/${candidateId}/review`),
  submitFeedback: (candidateId, feedbackData) => api.post(`/panelist/candidates/${candidateId}/feedback`, feedbackData),
  getPendingCVReviews: (params = {}) => {
        console.log('📝 API: Getting pending CV reviews:', params);
        return api.get('/panelist/cv-reviews/pending', { params });
    },
  getCandidateCVDetails: (candidateId) => api.get(`/panelist/candidates/${candidateId}/cv-details`),
  submitComprehensiveFeedback: (candidateId, feedbackData) => api.post(`/panelist/candidates/${candidateId}/comprehensive-feedback`, feedbackData),
  getFeedbackHistory: (params = {}) => api.get('/panelist/feedback-history', { params }),

  // Interview Management
  getInterviewFeedbackHistory: (params = {}) => {
    console.log('📝 API: Getting interview feedback history with params:', params);
    return api.get('/panelist/interview-feedback-history', { params });
  },

  getSharedUpcomingInterviews: () => api.get('/interviews/upcoming'),
  getSharedCompletedInterviews: (params = {}) => api.get('/interviews/completed', { params }),
  getSharedInterviewDetails: (interviewId) => api.get(`/interviews/${interviewId}/details`),
  completeInterview: (interviewId, feedbackData) => api.post(`/interviews/${interviewId}/complete`, feedbackData),
  getInterviewFeedback: (interviewId) => api.get(`/interviews/${interviewId}/feedback`),

  // Interview Management - Corrected to use panelist-specific endpoints
  getUpcomingInterviews: () => api.get('/panelist/interviews/upcoming'),
  getCompletedInterviews: (params = {}) => api.get('/panelist/interview-feedback-history', { params }),
  getInterviewDetails: (interviewId) => api.get(`/panelist/interviews/${interviewId}/details`),
  
  // ✅ NEW: Interview feedback and reschedule
  submitComprehensiveInterviewFeedback: (interviewId, feedbackData) => {
    console.log('🎯 API: Submitting comprehensive interview feedback:', interviewId, feedbackData);
    return api.post(`/panelist/interviews/${interviewId}/comprehensive-feedback`, feedbackData);
  },
  rescheduleInterview: (interviewId, rescheduleData) => {
    console.log('🔄 API: Rescheduling interview:', interviewId, rescheduleData);
    return api.put(`/panelist/interviews/${interviewId}/reschedule`, rescheduleData);
  },

  // Candidate Search & History
  searchCandidates: (params = {}) => api.get('/panelist/candidates/search', { params }),
  getCandidateCompleteHistory: (candidateId) => api.get(`/panelist/candidates/${candidateId}/complete-history`),

  // ✅ AVAILABILITY SLOT MANAGEMENT (ENHANCED)
  getAvailabilitySlots: (params = {}) => api.get('/panelist/availability/slots', { params }),
  debugAvailabilitySlots: () => api.get('/panelist/availability/debug'),

  // ✅ BASIC SLOT CREATION
  createAvailabilitySlots: (slotData) => api.post('/panelist/availability/create-slots', slotData),

  // ✅ ENHANCED SLOT CREATION WITH TEAMS & INVITATIONS
  createAvailabilitySlotsWithInvitations: (slotData) => {
    console.log('🎥 API: Creating enhanced slot with invitations:', slotData);
    return api.post('/panelist/availability/create-slots-with-invitations', slotData);
  },

  updateAvailabilitySlot: (slotId, slotData) => api.put(`/panelist/availability/slots/${slotId}`, slotData),
  deleteAvailabilitySlot: (slotId) => api.delete(`/panelist/availability/slots/${slotId}`),

  // Add to panelistAPI object
cleanupCompletedInterviewSlots: () => {
  console.log('🧹 API: Cleaning up completed interview slots');
  return api.post('/panelist/availability/cleanup-completed-interviews');
},



  // ✅ COLLEAGUE SEARCH FOR INVITATIONS
  searchColleagues: (searchTerm) => {
    console.log('🔍 API: Searching colleagues:', searchTerm);
    return api.get(`/panelist/colleagues/search?q=${encodeURIComponent(searchTerm)}`);
  },

  // Debug
};

// ==================== EMAIL API ====================
export const emailAPI = {
  sendInvitation: (emailData) => api.post('/email/send-invitation', emailData),
  sendConfirmation: (emailData) => api.post('/email/send-confirmation', emailData),
  sendCancellation: (emailData) => api.post('/email/send-cancellation', emailData),
};

// ==================== SYSTEM API ====================
export const systemAPI = {
  healthCheck: () => api.get('/health'),
  getRoutes: () => api.get('/routes'),
  testSessionSet: () => api.get('/test-session-set'),
};

// ==================== NOTIFICATIONS API ====================
export const notificationAPI = {
  getNotifications: () => api.get('/notifications/'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationIds) => api.post('/notifications/mark-read', { notification_ids: notificationIds })
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Generic API call with error handling
 */
export const apiCall = async (apiFunction, ...args) => {
  try {
    const response = await apiFunction(...args);
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('API Call Error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      status: error.response?.status || 500
    };
  }
};

/**
 * Upload file with progress tracking
 */
export const uploadFileWithProgress = (url, formData, onProgress) => {
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      if (onProgress) onProgress(percentCompleted);
    }
  });
};

/**
 * Download file with proper handling
 */
export const downloadFile = async (url, filename) => {
  try {
    const response = await api.get(url, { responseType: 'blob' });
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
    return { success: true };
  } catch (error) {
    console.error('Download error:', error);
    return { success: false, error: error.message };
  }
};

// ==================== CONVENIENCE EXPORTS (MAINTAINED FOR BACKWARD COMPATIBILITY) ====================

// Panelist convenience exports
export const getDashboard = panelistAPI.getDashboard;
export const getAssignedJobs = panelistAPI.getAssignedJobs;
export const getCVReviewsPending = panelistAPI.getPendingCVReviews;
export const getCandidateCVDetails = panelistAPI.getCandidateCVDetails;
export const submitComprehensiveFeedback = panelistAPI.submitComprehensiveFeedback;
export const getFeedbackHistory = panelistAPI.getFeedbackHistory;

// Auth convenience exports
export const checkAuthSession = authAPI.checkSession;
export const loginUser = authAPI.login;
export const logoutUser = authAPI.logout;

export default api;

// Export API_PREFIX for modules that use fetch directly
export { API_PREFIX };

// A global window.fetch patch used to live here, rewriting the literal
// 'http://localhost:5000/api' to API_PREFIX at runtime. The three call sites
// that needed it (components/admin/CandidateSearch.js) now import API_PREFIX
// directly, so monkey-patching fetch is no longer necessary.

// The axios baseURL is API_PREFIX, resolved once at module load from
// REACT_APP_API_BASE. Do not override it per-request.
